import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../../../../theme/utils';
import Flex from '../../../../../../components/Flex';
import { ProjectId } from '../../../../../../models/Types';
import { Empty } from '@prio365/prio365-react-library';
import { Project } from '../../../../../../models/Project';
import { useSelector } from 'react-redux';
import {
  getProject,
  RootReducerState,
} from '../../../../../../apps/main/rootReducer';
import PromanReportViewer from './PromanReportViewer';
import { ActionBar } from '@prio365/prio365-react-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePromanData } from './hooks/usePromanData';
import { usePromanNavigation } from './hooks/usePromanNavigation';
import { PromanSidebar } from './components/PromanSidebar';
import {
  getProjectExtensionMe,
  getReportDocuments,
  generatePromanUrl,
  PROMAN_WEBCLIENT_URL,
} from './utils/promanHelpers';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    padding: `${theme.old.spacing.defaultPadding}px ${
      theme.old.spacing.defaultPadding * 2
    }px`,
    backgroundColor: theme.colors.application.background.light,
  },
  sidebar: {
    flex: 1,
    maxWidth: '320px',
    width: '320px',
    overflowY: 'auto',
    backgroundColor: theme.colors.application.background.default,
    marginRight: theme.spacing.regular,
  },
  content: {
    overflow: 'hidden',
    height: '100%',
  },
  contentArea: {
    flex: 1,
    width: '100%',
  },
  title: {
    lineHeight: '44px!important',
    padding: `${theme.old.spacing.unit(2)}px ${
      theme.old.spacing.defaultPadding
    }px`,
    margin: '0!important',
    borderBottom: `1px solid ${theme.colors.application.border}`,
  },
  titleInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.old.spacing.unit(1),
  },
  titleText: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
  },
  reloadIcon: {
    cursor: 'pointer',
    color: theme.old.palette.primaryColor,
    fontSize: '16px',
    '&:hover': {
      color: theme.old.palette.primaryColor,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50px',
    marginTop: theme.spacing.regular,
  },
  emptyState: {
    padding: theme.old.spacing.unit(4),
    textAlign: 'center',
    backgroundColor: theme.colors.application.background.default,
    height: '100%',
  },
  collapseFolderPanel: {
    paddingTop: theme.spacing.regular,
    paddingLeft: theme.spacing.regular,
    paddingRight: theme.spacing.regular,
    '& .prio-collapse-item-active': {
      backgroundColor: theme.colors.application.background.light,
    },
  },
  collapseCollectionPanel: {
    gap: 0,
  },
  collectionHeader: {
    '& .prio-collapse-headerLabel': {
      fontWeight: theme.font.fontWeight.regular,
    },
    paddingLeft: 32,
  },
  reportsList: {
    maxHeight: '400px',
    overflowY: 'auto',
  },
  reportItem: {
    padding: theme.old.spacing.unit(1),
    color: theme.colors.application.typography.default,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.old.palette.backgroundPalette.hover,
    },
    '&.selected': {
      backgroundColor: theme.colors.application.background.selected,
    },
  },
  reportItemContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.old.spacing.unit(0.5),
    paddingLeft: 40,
  },
  reportItemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.old.spacing.unit(1),
    '& .prio-collapse-headerLabel': {
      fontWeight: theme.font.fontWeight.regular,
    },
  },
  reportCode: {
    fontSize: '14px',
  },
  reportDate: {
    fontSize: '12px',
  },
  reportPlace: {
    fontSize: '12px',
  },
  reportStatus: {
    fontSize: '11px',
    padding: '2px 6px',
    borderRadius: '3px',
    alignSelf: 'flex-start',
  },
  searchBar: {
    padding: theme.old.spacing.unit(2),
    borderBottom: `1px solid ${theme.colors.application.border}`,
  },
  pdfIcon: {
    color: '#d32f2f',
    fontSize: '14px',
  },
  nestedCollapse: {
    gap: 0,
  },
}));

interface PromanPageProps {
  projectId: ProjectId;
  className?: string;
}

const PromanPage: React.FC<PromanPageProps> = (props) => {
  const classes = useStyles();
  const { className, projectId } = props;
  const { t } = useTranslation();

  // Get project data
  const project = useSelector<RootReducerState, Project>((state) =>
    getProject(state, projectId)
  );

  const promanProjectExtension = useMemo(() => {
    return getProjectExtensionMe(project, 'proman');
  }, [project]);

  const promanProjectId = parseInt(promanProjectExtension?.key || '0');

  // Initialize navigation hook
  const navigation = usePromanNavigation(
    [], // Will be updated when data loads
    undefined // Collection expand callback
  );

  // Initialize data hook with navigation state
  const data = usePromanData(promanProjectId, navigation.activeCollections);

  // Update navigation with loaded data
  const navigationWithData = usePromanNavigation(
    data.reportCollections,
    data.loadReportsForCollection
  );

  // Handle search query change
  const handleSearchChange = useCallback(
    (query: string) => {
      // If search is active, start loading all collections immediately
      if (query.trim() !== '') {
        data.startLoadingAllReports();
      }
      // Set search query
      navigationWithData.setSearchQuery(query);
    },
    [data, navigationWithData]
  );

  // Event handlers
  const handleOpenProman = () => {
    try {
      const url = generatePromanUrl(
        data.mandateId,
        promanProjectId,
        navigationWithData.folderGroups,
        navigationWithData.selectedReport
      );
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening PROMAN:', err);
    }
  };

  const handleOpenPromanWebClient = () => {
    try {
      window.open(PROMAN_WEBCLIENT_URL, '_blank');
    } catch (err) {
      console.error('Error opening PROMAN WebClient:', err);
    }
  };

  // Render content area
  const renderContent = () => {
    if (!navigationWithData.selectedReport) {
      return (
        <div className={classes.emptyState}>
          <Empty
            customMessage={t('projects:addIn.extensions.proman.selectReport')}
          />
        </div>
      );
    }

    const reportDocuments = getReportDocuments(
      navigationWithData.selectedReport,
      data.organizedDocuments
    );

    return (
      <PromanReportViewer
        report={navigationWithData.selectedReport}
        documents={reportDocuments}
        mandant={data.mandateId}
        promanProjectId={promanProjectId}
      />
    );
  };

  return (
    <Flex.Column className={classNames(classes.root, className)}>
      <Flex.Row className={classes.content}>
        <Flex.Column className={classes.sidebar}>
          <h1 className={classes.title}>
            <div className={classes.titleInner}>
              <div className={classes.titleText}>
                {t('projects:addIn.extensions.proman.protocols')}
              </div>
              <div>
                <FontAwesomeIcon
                  className={classes.reloadIcon}
                  icon={['fal', 'sync']}
                  onClick={data.reloadAllData}
                  title={t('projects:addIn.extensions.proman.reload')}
                />
              </div>
            </div>
          </h1>
          <PromanSidebar
            folderGroups={navigationWithData.folderGroups}
            folders={data.promanProjectFolders}
            reportsByCollection={data.reportsByCollection}
            organizedDocuments={data.organizedDocuments}
            loading={data.loading}
            loadingReports={data.loadingReports}
            loadingAllReports={data.loadingAllReports}
            reloadKey={data.reloadKey}
            activeFolders={navigationWithData.activeFolders}
            activeCollections={navigationWithData.activeCollections}
            activeReports={navigationWithData.activeReports}
            selectedReportId={
              navigationWithData.selectedReport?.reportId || null
            }
            searchQuery={navigationWithData.searchQuery}
            onFolderChange={navigationWithData.handleFolderChange}
            onCollectionChange={navigationWithData.handleCollectionChange}
            onReportChange={navigationWithData.handleReportChange}
            onReportClick={navigationWithData.handleReportClick}
            onSearchChange={handleSearchChange}
            classes={classes}
          />
        </Flex.Column>

        <div className={classes.contentArea}>
          <div style={{ padding: '0 0 12px 0' }}>
            <ActionBar
              buttons={[
                {
                  id: 'openInNewTabButton',
                  size: 'default',
                  shape: 'default',
                  type: 'primary',
                  children: t('projects:addIn.extensions.proman.openInNewTab'),
                  onClick: handleOpenProman,
                },
                {
                  id: 'openWebClientButton',
                  size: 'default',
                  shape: 'default',
                  type: 'default',
                  children: 'PROMAN WebClient öffnen',
                  onClick: handleOpenPromanWebClient,
                },
              ]}
            />
          </div>

          <div style={{ height: 'calc(100% - 48px)' }}>{renderContent()}</div>
        </div>
      </Flex.Row>
    </Flex.Column>
  );
};

export default PromanPage;
